// 2026-05-26 - FormuLearning - Step 4 Runs 알고리즘 선택 + 생성
// 2026-08-14 이동 - 이슈 #60(M84-71): 렌더 소관이 Step 4(`Step4Card.tsx`)인데 파일만
//                   `features/step3/`에 남아 있어 소재가 오해를 불렀다. 소관에 맞게 이동.
//                   순수 이동이며 동작 변경 없음.
// 2026-09-09 수정 - 이슈 #72(M901-19): **약어를 풀어 쓴다.** `LHS` 만으로는 무엇인지
//                   알 수 없어 회의에서 지적됐다(*"lhs (latin hypercube sampling) 이름을
//                   풀어 적고"*). 우측 설명 칸도 함께 연다 — **문구·그림은 국민대가
//                   전달 예정**이므로 자리만 만들고 지금은 한 줄 요지로 채운다.
// 2026-09-21 수정 - 이슈 #20(F-9): **실험 횟수 하한 D+1**(BR-S3-09). LHS·Random 은 변동 X 변수
//                   D 개에 대해 최소 D+1 회가 필요하다 — 미달이면 생성 버튼을 막고 사유를 보인다.
//                   권장 범위(3D~5D)는 한 줄 도움말로 둔다. 원 이슈의 눈금 슬라이더·음영은 화면
//                   정본(UI/UX v2.3 §4.1 숫자 입력)에 없어 제외했다. 서버도 같은 하한으로 422 를 낸다.
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 알고리즘 선택·Runs 입력·초기점 생성 비활성(설명 칸은 그대로).
// 2026-11-03 수정 - 이슈 #102: ① 총중량이 없으면 Generate 를 막고 «총중량을 먼저 입력하십시오» 를 보인다(D11,
//                   장지연 교수 261103 — 서버도 422) ② User Define 설명에 «고정값만 채워지고 나머지는 빈칸» 을 적는다(M1002-05).
// 파일 위치: frontend/src/features/step4/RunsSection.tsx

import { useState } from 'react'
import type { XVariable } from '../../api/step3'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

type Algorithm = 'lhs' | 'random' | 'user_defined'

interface Props {
  algorithm: Algorithm | null
  runsCount: number | null
  onAlgorithmChange: (algo: Algorithm) => void
  onCountChange: (n: number) => void
  onGenerate: () => Promise<void>
  generating: boolean
  xVariables: XVariable[]
  /** Step 3 총중량 — 없으면 생성하지 않는다(#102 D11). */
  totalWeightMg: number | null
}

// 범위 안에서 변동하는 X 변수 모드 — 설계 공간의 차원 D. 서버 `doe_runner.VARYING_MODES` 와 같다.
const VARYING_MODES: XVariable['mode'][] = ['selected', 'selected(non-weight)']

const ALGORITHMS: { value: Algorithm; label: string; full: string; brief: string }[] = [
  {
    value: 'lhs',
    label: 'Latin Hypercube Sampling (LHS)',
    full: 'Latin Hypercube Sampling',
    brief: '각 변수의 범위를 실험 횟수만큼 균등히 나누고 구간마다 한 점씩만 뽑습니다. '
      + '난수와 달리 한쪽에 몰리거나 빈 구간이 생기지 않아, 같은 실험 횟수로 설계 공간을 더 고르게 훑습니다.',
  },
  {
    value: 'random',
    label: 'Random',
    full: 'Random Sampling',
    brief: '범위 안에서 제약 없이 무작위로 뽑습니다. 치우침을 보정하지 않으므로 실험 횟수가 적으면 '
      + '점들이 몰리거나 빈 구간이 생길 수 있습니다.',
  },
  {
    value: 'user_defined',
    label: 'User Define',
    full: 'User Defined',
    brief: '사용자가 설계점을 직접 정합니다. 기존 처방을 출발점으로 삼을 때 씁니다. '
      + '생성하면 고정(fixed) 값만 채워지고 나머지는 빈칸입니다 — 아래 성분표에서 Step 3 X 범위 안의 값으로 모두 채워야 '
      + 'Next Optimization 을 실행할 수 있습니다. Balanced 성분은 나머지가 다 차면 자동 계산됩니다.',
  },
]

export default function RunsSection({ algorithm, runsCount, onAlgorithmChange, onCountChange, onGenerate, generating, xVariables, totalWeightMg }: Props) {
  const canWrite = useCanWrite()
  const noWeight = !(typeof totalWeightMg === 'number' && totalWeightMg > 0)
  const roTitle = canWrite ? undefined : READ_ONLY_TITLE
  const [localCount, setLocalCount] = useState(runsCount ?? 8)
  const selected = ALGORITHMS.find((a) => a.value === algorithm) ?? null
  // 하한은 LHS·Random 에만 건다. User Define 은 설계점을 사람이 정한다(S3-RUNS-02).
  const bounded = algorithm === 'lhs' || algorithm === 'random'
  const dim = xVariables.filter((x) => VARYING_MODES.includes(x.mode)).length
  const minRuns = dim + 1
  const belowMin = bounded && localCount < minRuns

  return (
    <div className="mb-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
        Initial Design Algorithm
      </h4>
      <div
        className="grid gap-3 rounded border p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,15rem)]"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <div>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="text-[11px] text-text-secondary">Algorithm:</span>
          <div className="flex rounded overflow-hidden" style={{ border: '1px solid var(--border-soft)' }}>
            {ALGORITHMS.map((a) => (
              <button
                key={a.value}
                onClick={() => onAlgorithmChange(a.value)}
                disabled={!canWrite}
                title={roTitle}
                className="px-3 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-60"
                style={
                  algorithm === a.value
                    ? { background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }
                    : { color: 'var(--color-text-secondary)' }
                }
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[11px] text-text-secondary">Runs:</span>
          <input
            type="number"
            value={localCount}
            onChange={(e) => {
              const v = Number(e.target.value)
              setLocalCount(v)
              onCountChange(v)
            }}
            className="w-16 rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none"
            style={{ border: '1px solid var(--border-soft)' }}
            min={bounded ? minRuns : 1}
            disabled={!canWrite}
            title={roTitle}
          />
          <button
            onClick={onGenerate}
            disabled={generating || !algorithm || belowMin || noWeight || !canWrite}
            title={roTitle ?? (noWeight ? '총중량을 먼저 입력하십시오 (Step 3 Target Design)' : undefined)}
            className="rounded bg-accent-purple px-4 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate Initial Points'}
          </button>
        </div>
        {noWeight && (
          <p className="mt-1.5 text-[10px] text-danger" data-testid="runs-total-weight-required">
            총중량을 먼저 입력하십시오 — Step 3 Target Design 의 총중량(mg)이 있어야 초기 설계를 만들 수 있습니다.
          </p>
        )}
        {bounded && dim > 0 && (
          <p className={`mt-1.5 text-[10px] ${belowMin ? 'text-danger' : 'text-text-muted'}`}>
            {belowMin && `최소 ${minRuns}회 이상이어야 생성할 수 있습니다. `}
            최소 {minRuns}회 · 권장 {3 * dim}~{5 * dim}회 (변동 X 변수 {dim}개 기준)
          </p>
        )}
        </div>

        {/* 우측 설명 칸(M901-19). 그림은 국민대 전달 대기 — 도착하면 이 자리에 넣는다. */}
        <aside
          className="rounded p-2.5"
          style={{ background: 'var(--color-bg-panel)', border: '1px solid var(--border-subtle)' }}
        >
          {selected ? (
            <>
              <p className="mb-1 text-[11px] font-semibold text-text-body">{selected.full}</p>
              <p className="text-[10px] leading-relaxed text-text-secondary">{selected.brief}</p>
            </>
          ) : (
            <p className="text-[10px] text-text-muted">알고리즘을 고르면 설명이 표시됩니다.</p>
          )}
        </aside>
      </div>
    </div>
  )
}
